import React from 'react'
import { useRouteName } from '@cedarjs/router'

type CmdkData = Record<string, unknown>

type CmdkContextValue = {
  data: CmdkData
  setData: React.Dispatch<React.SetStateAction<CmdkData>>
  reset: () => void
}

const CmdkContext = React.createContext<CmdkContextValue | undefined>(undefined)

export const CmdkProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = React.useState<CmdkData>({})

  const reset = React.useCallback(() => setData({}), [])

  // Clear on route change to avoid leaking data across pages
  const routeName = useRouteName()
  React.useEffect(() => {
    reset()
  }, [routeName, reset])

  const value = React.useMemo<CmdkContextValue>(
    () => ({ data, setData, reset }),
    [data, setData, reset]
  )

  return <CmdkContext.Provider value={value}>{children}</CmdkContext.Provider>
}

export const useCmdk = () => {
  const ctx = React.useContext(CmdkContext)
  if (!ctx) throw new Error('useCmdk must be used within a CmdkProvider')
  return ctx
}

export const useCmdkData = <T extends CmdkData>(data: T) => {
  const { setData } = useCmdk()
  React.useEffect(() => {
    setData((prev) => {
      // Shallow-merge only changed keys to avoid unnecessary re-renders
      let changed = false
      const next: CmdkData = { ...prev }
      for (const key of Object.keys(data)) {
        if (next[key] !== data[key]) {
          next[key] = data[key]
          changed = true
        }
      }
      return changed ? next : prev
    })
    // Note: Prefer passing a memoized object into this hook to avoid identity churn
  }, [data, setData])
}
